import { WarningTwoIcon } from "@chakra-ui/icons";
import {
  GridItem,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect } from "react";
import { QuoteApi } from "../../api/QuoteApi";
import useQuote from "../../hooks/useQuote";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { SelectItem } from "../../lib/types/SelectItem";
import SelectField from "../form-fields/SelectField";
import ExceptionSubmitButton from "./ExceptionSubmitButton";

export type ExceptionOptionsProps = {
  handler: (status: string | undefined) => void;
  quoteType: any;
  disableInputs: boolean;
  ownerBalance?: number;
  loanAmount?: number;
};

function AutoException({
  handler,
  quoteType,
  disableInputs,
  ownerBalance,
  loanAmount,
}: ExceptionOptionsProps) {
  const { state, dispatch } = useQuote();
  const { metadata } = state;
  const firstUpdate = React.useRef(true);
  const _isMounted = React.useRef(true);
  const { setFieldValue, values, dirty } = useFormikContext<any>();
  const [isLoading, setIsLoading] = React.useState(false);
  const { autoFee, autoRate, commissionReduction, loanProduct, productType } =
    values;
  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);
  const { colorMode } = useColorMode();
  const noException = autoFee.toString() === "0" && autoRate.toString() === "0";

  const resetCalculationStatus = React.useCallback(() => {
    dispatch({
      type: "QUOTE_CALCULATION_RESET",
    });
  }, [dispatch]);

  const formatNumberWithCommas = (x: number | undefined) => {
    if (x === undefined) return;

    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const fetchAutoCommission = useCallback(() => {
    if (!metadata?.ownerRole) return;

    if (noException) {
      setFieldValue("commissionReduction", "0.000");
      return;
    }

    setIsLoading(true);

    QuoteApi.getAutoCommission(
      metadata?.ownerRole,
      autoFee,
      autoRate,
      quoteType,
      loanProduct ? loanProduct : productType
    )

      .then((res) => {
        const formattedCommissionReduction = res.data.commission_reduction;

        if (_isMounted.current || firstUpdate.current) {
          formattedCommissionReduction
            ? setFieldValue(
                "commissionReduction",
                Number(formattedCommissionReduction).toFixed(2).toString()
              )
            : setFieldValue("commissionReduction", "0.000");
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("err: 💥", err);
      });
  }, [
    autoFee,
    autoRate,
    quoteType,
    setFieldValue,
    metadata?.ownerRole,
    loanProduct,
    noException,
    productType,
  ]);

  useEffect(() => {
    if (metadata?.exceptionStatus !== "Auto Approved" && _isMounted.current) {
      if (!autoFee && !autoRate && firstUpdate.current) return;
      fetchAutoCommission();
      if (dirty) {
        resetCalculationStatus();
      }
    }
  }, [
    metadata?.exceptionStatus,
    fetchAutoCommission,
    autoFee,
    autoRate,
    dirty,
    resetCalculationStatus,
  ]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    fetchAutoCommission();
  }, [autoFee, autoRate, fetchAutoCommission]);

  React.useEffect(() => {
    return () => {
      _isMounted.current = false;
    };
  }, []);

  const isLoanAmountUnderMin = React.useMemo(() => {
    if (loanAmount && loanAmount < 150000) {
      return true;
    }
    return false;
  }, [loanAmount]);

  const isBalanceUnderLoanAmt = React.useMemo(() => {
    if (loanAmount && ownerBalance && loanAmount > ownerBalance) {
      return true;
    }
    return false;
  }, [loanAmount, ownerBalance]);

  return (
    <>
      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SelectField
          label="Auto Fee Exception"
          name="autoFee"
          placeholder=""
          data={selectRate}
          disabled={disableInputs}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SelectField
          label="Auto Rate Exception"
          name="autoRate"
          placeholder=""
          data={selectRate}
          disabled={disableInputs}
        />
      </GridItem>

      <GridItem
        boxShadow="base"
        minWidth={"170px"}
        paddingY="6"
        rounded="md"
        background={bgColor}
        border={colorMode === "dark" ? "1px solid white" : "none"}
        textAlign="center"
        colSpan={{ base: 12, lg: 4 }}
      >
        <Text marginBottom={2} fontSize="xs" fontWeight="medium">
          Commission Reduction
        </Text>

        {isLoading ? (
          <Stack justifyContent="center" direction="row">
            <Spinner
              thickness="2px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.500"
              size="sm"
            />
            <Text fontSize="sm">Loading...</Text>
          </Stack>
        ) : (
          <Text fontWeight="500">
            {commissionReduction ? commissionReduction : "0.000"}%
          </Text>
        )}
      </GridItem>

      <GridItem
        colSpan={{ base: 12, lg: 4 }}
        boxShadow="base"
        rounded="md"
        background={bgColor}
        border={colorMode === "dark" ? "1px solid white" : "none"}
        textAlign="center"
        minWidth={"170px"}
        paddingY="6"
      >
        <Text marginBottom={2} fontSize="xs" fontWeight="medium">
          Total Loan Amount
        </Text>
        <Text
          fontWeight="500"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {isLoanAmountUnderMin && (
            <Tooltip
              label="Below minimum loan amount"
              fontSize="sm"
              hasArrow
              bg="red.100"
              textColor={"red.500"}
            >
              <WarningTwoIcon cursor="pointer" textColor="red.500" mr={2} />
            </Tooltip>
          )}
          ${loanAmount ? formatNumberWithCommas(loanAmount) : "0.00"}
        </Text>
      </GridItem>

      <GridItem
        colSpan={{ base: 12, lg: 4 }}
        boxShadow="base"
        rounded="md"
        background={bgColor}
        border={colorMode === "dark" ? "1px solid white" : "none"}
        textAlign="center"
        paddingY="6"
        minWidth={"170px"}
      >
        <Text marginBottom={2} fontSize="xs" fontWeight="medium">
          Auto Exception Balance
        </Text>
        <Text
          fontWeight="500"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {isBalanceUnderLoanAmt && (
            <Tooltip
              label="Below total loan amount"
              fontSize="sm"
              hasArrow
              bg="red.100"
              textColor={"red.500"}
            >
              <WarningTwoIcon cursor="pointer" textColor="red.500" mr={2} />
            </Tooltip>
          )}
          ${ownerBalance ? formatNumberWithCommas(ownerBalance) : "0.00"}
        </Text>
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 12 }} marginLeft={{ base: 0, lg: 12 }}>
        <ExceptionSubmitButton
          contentText={
            "Once an exception is submitted deductions from your exception balance cannot be reverted."
          }
          status={"Auto Approved"}
          handler={handler}
          loanAmount={loanAmount}
          noException={noException}
        />
      </GridItem>
    </>
  );
}

export default AutoException;

export const selectRate: SelectItem[] = [
  { label: "0.000%", value: "0" },
  { label: "0.120%", value: "0.12" },
  { label: "0.250%", value: "0.25" },
];
